/**
 * A clearly labelled workflow illustration for ScriptTrack.
 *
 * This is intentionally not a screenshot and does not show fabricated customer
 * data, compliance outcomes, live status, or performance numbers.
 */
export default function ScriptTrackMockup({
  variant = "compliance",
  compact = false,
}: {
  variant?: "compliance" | "inventory" | "dispensing";
  compact?: boolean;
}) {
  const variantCopy = {
    compliance: {
      eyebrow: "Free DSCSA compliance workflow",
      title: "Organize the review",
      rows: [
        ["Transaction information", "Organize", "TI + TS workspace"],
        ["Receiving review", "Review", "Product-identifier checks"],
        ["Team next step", "Plan", "Keep follow-up visible"],
      ],
    },
    inventory: {
      eyebrow: "Atlas Inventory workflow",
      title: "See inventory work",
      rows: [
        ["Stock tracking", "Monitor", "Inventory operations"],
        ["Purchase orders", "Plan", "Replenishment workflow"],
        ["Counting", "Review", "Cycle count workspace"],
      ],
    },
    dispensing: {
      eyebrow: "ScriptTrack Cash workflow",
      title: "Connect dispensing work",
      rows: [
        ["Patient management", "Organize", "Practice workflow"],
        ["Prescription workflow", "Review", "Dispensing operations"],
        ["Point of sale", "Plan", "Cash-pay workflow"],
      ],
    },
  }[variant];

  return (
    <div
      className={`relative w-full ${compact ? "max-w-xs" : "max-w-sm"} mx-auto`}
      aria-label={variant === "compliance" ? "Illustration of the free DSCSA compliance software" : "Illustration of a paid ScriptTrack workflow"}
    >
      <div
        className="absolute inset-0 rounded-2xl blur-2xl opacity-30"
        style={{ background: "radial-gradient(ellipse at 60% 40%, #10b981 0%, #054FF0 60%, transparent 100%)" }}
        aria-hidden="true"
      />

      <div className="relative bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden">
        <div className="bg-primary px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2" aria-hidden="true">
            <div className="w-2 h-2 rounded-full bg-red-400" />
            <div className="w-2 h-2 rounded-full bg-yellow-400" />
            <div className="w-2 h-2 rounded-full bg-green-400" />
          </div>
          <span className="text-white/80 text-xs font-medium">{variant === "compliance" ? "DSCSA software workflow illustration" : "ScriptTrack workflow illustration"}</span>
          <div className="w-12" aria-hidden="true" />
        </div>

        <div className="px-5 py-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-gray-600 font-medium uppercase tracking-widest mb-0.5">
                {variantCopy.eyebrow}
              </p>
              <p className="text-primary font-bold text-sm">{variantCopy.title}</p>
            </div>
            <span className="text-[11px] font-semibold text-gray-500 border border-gray-200 rounded-full px-2.5 py-1 whitespace-nowrap">
              Example view
            </span>
          </div>

          <div className="border-t border-gray-100" />

          <div className="space-y-3">
            {variantCopy.rows.map(([label, value, sub], index) => (
              <div key={label} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-accent">{String(index + 1).padStart(2, "0")}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500 truncate">{label}</span>
                    <span className="text-sm font-bold text-primary flex-shrink-0">{value}</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">{sub}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-100" />
          <p className="text-[11px] text-gray-500">
            Illustrative workflow only. Features vary by selected product and account.
          </p>
        </div>
      </div>
    </div>
  );
}